// ── CareNav Design System — Stonewashed Neutrals ─────────────────────────────
// Palette: Sand, Warm Slate, Clay
// Type: Playfair Display (display) + DM Sans (body)
import { useState, useEffect } from "react";

export const theme = {
  // ── Backgrounds ─────────────────────────────────────────────────────────────
  bg:           "#F5F0E8",   // sand — warm base
  bgDeep:       "#EDE7DB",   // deeper sand for section contrast
  bgDark:       "#2A2520",   // near-black warm brown for dark sections
  surface:      "#FDFAF5",   // near-white warm card surface
  surfaceAlt:   "#EDE7DB",   // alt surface — slightly deeper sand

  // ── Borders ─────────────────────────────────────────────────────────────────
  border:       "#2A2520",
  borderLight:  "#2A2520",

  // ── Text ────────────────────────────────────────────────────────────────────
  text:         "#1E1A16",   // warm near-black
  textMid:      "#6B6560",   // warm slate mid
  textLight:    "#8B8680",   // warm slate light
  textInverse:  "#F5F0E8",   // text on dark bg

  // ── Accent — Clay ───────────────────────────────────────────────────────────
  clay:         "#C4714A",   // primary clay accent
  clayDark:     "#A85C38",   // darker clay for hover
  clayLight:    "#F2E4D8",   // clay tint bg
  clayMid:      "#D4896A",   // lighter clay

  // ── Secondary ───────────────────────────────────────────────────────────────
  slate:        "#8B8680",   // warm slate
  slateLight:   "#E8E3DA",
  slateDark:    "#5C5852",

  navy:         "#1E2A3A",   // deep navy for contrast headings
  navyLight:    "#E8ECF2",

  // ── Status colours (warm-toned) ──────────────────────────────────────────
  green:        "#4A7C5A",
  greenLight:   "#DFF0E4",
  amber:        "#C4884A",
  amberLight:   "#F5EAD8",
  red:          "#B84A3A",
  redLight:     "#F5E0DC",

  // ── Care levels ─────────────────────────────────────────────────────────────
  selfCare:   { bg: "#DFF0E4", text: "#2A5C38", dot: "#4A7C5A" },
  pharmacy:   { bg: "#E8ECF2", text: "#1E2A3A", dot: "#4A6A8A" },
  gp:         { bg: "#F5EAD8", text: "#7A4A1A", dot: "#C4884A" },
  emergency:  { bg: "#F5E0DC", text: "#7A2A1E", dot: "#B84A3A" },
  dentist:    { bg: "#EDE0F0", text: "#4A1A6A", dot: "#8A5AA0" },
  nhs111:     { bg: "#F5EAD8", text: "#7A4A1A", dot: "#C4884A" },

  // ── Typography ──────────────────────────────────────────────────────────────
  fontDisplay: "'Playfair Display', Georgia, serif",
  fontBody:    "'DM Sans', system-ui, sans-serif",

  // ── Shape ───────────────────────────────────────────────────────────────────
  radius:     "10px",
  radiusSm:   "6px",
  radiusLg:   "16px",
  radiusPill: "100px",

  // ── Shadow ──────────────────────────────────────────────────────────────────
  shadow:   "0 1px 3px rgba(30,26,22,0.06), 0 4px 12px rgba(30,26,22,0.04)",
  shadowMd: "0 2px 8px rgba(30,26,22,0.08), 0 8px 28px rgba(30,26,22,0.06)",
  shadowLg: "0 4px 16px rgba(30,26,22,0.08), 0 24px 56px rgba(30,26,22,0.08)",
};

// ── Care level helper ─────────────────────────────────────────────────────────
export function getCareConfig(careLevel) {
  const map = {
    "self-care":  { label: "Self-Care",     ...theme.selfCare },
    "pharmacy":   { label: "Pharmacy",      ...theme.pharmacy },
    "gp":         { label: "See a GP",      ...theme.gp },
    "nhs111":     { label: "Call 111",      ...theme.nhs111 },
    "emergency":  { label: "Call 999",      ...theme.emergency },
    "dentist":    { label: "See a Dentist", ...theme.dentist },
  };
  return map[careLevel] || { label: "Get Advice", ...theme.nhs111 };
}

// ── Global Styles ─────────────────────────────────────────────────────────────
export function GlobalStyles() {
  return (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,600;1,400;1,500&family=DM+Sans:wght@300;400;500;600;700&display=swap');
      *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
      body { background: #F5F0E8; font-family: 'DM Sans', system-ui, sans-serif; color: #1E1A16; -webkit-font-smoothing: antialiased; }
      @keyframes spin    { to { transform: rotate(360deg); } }
      @keyframes fadeUp  { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
      @keyframes bounce  { 0%, 60%, 100% { transform: translateY(0); } 30% { transform: translateY(-5px); } }
      @keyframes pulse   { 0%, 100% { opacity: 1; } 50% { opacity: 0.35; } }
      a { color: inherit; }
      ::selection { background: #F2E4D8; color: #C4714A; }
    `}</style>
  );
}



export function useWindowWidth() {
  const [width, setWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );
  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return width;
}

// ── Nav ───────────────────────────────────────────────────────────────────────
// ── Updated Nav with Mobile Support ──────────────────────────────────────────
export function Nav({ active = "" }) {
  const width = useWindowWidth();
  const isMobile = width < 768;
  const [isOpen, setIsOpen] = useState(false);

  // Close menu when resizing to desktop
  useEffect(() => {
    if (!isMobile) setIsOpen(false);
  }, [isMobile]);

  const navLinks = [
    ["Home", "/"],
    ["Conditions", "/conditions"],
    ["About", "/about"]
  ];

  return (
    <nav style={{
      background: theme.surface,
      borderBottom: `1px solid ${theme.border}`,
      padding: isMobile ? "0 1.25rem" : "0 2rem",
      display: "flex", alignItems: "center", justifyContent: "space-between",
      height: "64px",
      position: "sticky", top: 0, zIndex: 1000,
    }}>
      {/* Brand */}
      <a href="/" style={{ display: "flex", alignItems: "center", gap: "0.65rem", textDecoration: "none" }}>
        <span style={{ 
          fontFamily: theme.fontDisplay, 
          fontWeight: "600", 
          fontSize: "1.1rem", 
          color: theme.text, 
          letterSpacing: "0.01em" 
        }}>CareNav</span>
      </a>

      {/* Desktop Links */}
      {!isMobile && (
        <div style={{ display: "flex", alignItems: "center", gap: "2rem" }}>
          {navLinks.map(([label, path]) => (
            <a key={label} href={path} style={{
              color: active === label.toLowerCase() ? theme.clay : theme.textMid,
              textDecoration: "none", fontSize: "0.87rem", fontFamily: theme.fontBody,
              fontWeight: active === label.toLowerCase() ? "600" : "400",
              borderBottom: active === label.toLowerCase() ? `1.5px solid ${theme.clay}` : "1.5px solid transparent",
              paddingBottom: "2px", transition: "all 0.15s",
            }}
              onMouseEnter={e => { e.currentTarget.style.color = theme.clay; }}
              onMouseLeave={e => { e.currentTarget.style.color = active === label.toLowerCase() ? theme.clay : theme.textMid; }}
            >{label}</a>
          ))}
          <button
            onClick={() => window.dispatchEvent(new CustomEvent("openChatWidget"))}
            style={{ 
              background: theme.clay, color: "#fff", border: "none", 
              padding: "0.5rem 1.2rem", borderRadius: theme.radiusPill, 
              fontSize: "0.84rem", fontWeight: "600", cursor: "pointer" 
            }}
          >Check symptoms</button>
        </div>
      )}

      {/* Mobile Menu Toggle */}
      {isMobile && (
        <button 
          onClick={() => setIsOpen(!isOpen)}
          style={{ 
            background: "none", border: "none", cursor: "pointer", 
            padding: "0.5rem", display: "flex", flexDirection: "column", gap: "4px" 
          }}
        >
          <div style={{ width: "22px", height: "2px", background: theme.text, transition: "0.3s", transform: isOpen ? "rotate(45deg) translate(4px, 4px)" : "none" }} />
          <div style={{ width: "22px", height: "2px", background: theme.text, opacity: isOpen ? 0 : 1 }} />
          <div style={{ width: "22px", height: "2px", background: theme.text, transition: "0.3s", transform: isOpen ? "rotate(-45deg) translate(4px, -4px)" : "none" }} />
        </button>
      )}

      {/* Mobile Drawer */}
      {isMobile && isOpen && (
        <div style={{
          position: "fixed", top: "64px", left: 0, right: 0, bottom: 0,
          background: theme.surface, padding: "2rem",
          display: "flex", flexDirection: "column", gap: "1.5rem",
          animation: "fadeUp 0.3s ease-out", zIndex: 999
        }}>
          {navLinks.map(([label, path]) => (
            <a key={label} href={path} style={{
              fontSize: "1.2rem", fontWeight: "500", textDecoration: "none",
              color: active === label.toLowerCase() ? theme.clay : theme.text,
              borderBottom: `1px solid ${theme.borderLight}`, paddingBottom: "0.5rem"
            }}>{label}</a>
          ))}
          <button
            onClick={() => {
              setIsOpen(false);
              window.dispatchEvent(new CustomEvent("openChatWidget"));
            }}
            style={{ 
              background: theme.clay, color: "#fff", border: "none", 
              padding: "1rem", borderRadius: theme.radius, 
              fontSize: "1rem", fontWeight: "600", marginTop: "1rem" 
            }}
          >Check symptoms</button>
        </div>
      )}
    </nav>
  );
}

// ── Footer ────────────────────────────────────────────────────────────────────
export function Footer() {
  const cols = [
    { h: "CareNav",  links: [["About", "/about"], ["Conditions", "/conditions"], ["How it works", "/about"]] },
    { h: "Legal",    links: [["Privacy policy", "/privacy"], ["Terms of use", "/terms"], ["Accessibility", "/accessibility"]] },
    { h: "Support",  links: [["Contact", "/contact"], ["NHS 111", "https://111.nhs.uk"], ["Emergency: 999", "tel:999"]] },
  ];
  return (
    <footer style={{ background: theme.bgDark, padding: "3.5rem 2rem 2rem", marginTop: "5rem" }}>
      <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr", gap: "2rem", marginBottom: "2.5rem" }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", marginBottom: "1rem" }}>
              <div style={{ width: "26px", height: "26px", borderRadius: "6px", background: theme.clay, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <span style={{ color: "#fff", fontSize: "0.62rem", fontWeight: "700", fontFamily: theme.fontBody }}>CN</span>
              </div>
              <span style={{ fontFamily: theme.fontDisplay, fontWeight: "500", fontSize: "0.95rem", color: "#fff" }}>CareNav</span>
            </div>
            <p style={{ color: "rgba(245,240,232,0.4)", fontSize: "0.8rem", lineHeight: 1.7, margin: "0 0 0.5rem", maxWidth: "220px", fontFamily: theme.fontBody }}>
              AI-powered symptom guidance. Not a medical service.
            </p>
            <p style={{ color: "rgba(245,240,232,0.22)", fontSize: "0.72rem", margin: 0, fontFamily: theme.fontBody }}>University of Chester — CO6008</p>
          </div>
          {cols.map(({ h, links }) => (
            <div key={h}>
              <p style={{ color: "rgba(245,240,232,0.35)", fontSize: "0.68rem", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.1em", margin: "0 0 0.8rem", fontFamily: theme.fontBody }}>{h}</p>
              {links.map(([label, href]) => (
                <a key={label} href={href} style={{ display: "block", color: "rgba(245,240,232,0.55)", fontSize: "0.83rem", textDecoration: "none", marginBottom: "0.4rem", fontFamily: theme.fontBody, transition: "color 0.15s" }}
                  onMouseEnter={e => e.currentTarget.style.color = theme.clayMid}
                  onMouseLeave={e => e.currentTarget.style.color = "rgba(245,240,232,0.55)"}
                >{label}</a>
              ))}
            </div>
          ))}
        </div>
        <div style={{ borderTop: "1px solid rgba(245,240,232,0.07)", paddingTop: "1.5rem", display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: "0.5rem" }}>
          <p style={{ color: "rgba(245,240,232,0.2)", fontSize: "0.72rem", margin: 0, fontFamily: theme.fontBody }}>© 2025 CareNav. General guidance only — not a substitute for professional medical advice.</p>
          <p style={{ color: "rgba(245,240,232,0.2)", fontSize: "0.72rem", margin: 0, fontFamily: theme.fontBody }}>Emergency? Call <strong style={{ color: theme.red }}>999</strong></p>
        </div>
      </div>
    </footer>
  );
}

// ── Page Header ───────────────────────────────────────────────────────────────
export function PageHeader({ eyebrow, title, subtitle }) {
  return (
    <div style={{ padding: "4rem 2rem 3rem", background: theme.bg, borderBottom: `1px solid ${theme.border}` }}>
      <div style={{ maxWidth: "860px", margin: "0 auto" }}>
        {eyebrow && <p style={{ color: theme.clay, fontSize: "0.72rem", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.12em", margin: "0 0 0.75rem", fontFamily: theme.fontBody }}>{eyebrow}</p>}
        <h1 style={{ fontFamily: theme.fontDisplay, fontSize: "clamp(2rem, 4vw, 2.8rem)", fontWeight: "500", color: theme.text, margin: "0 0 0.75rem", lineHeight: 1.2, letterSpacing: "0.01em" }}>{title}</h1>
        {subtitle && <p style={{ color: theme.textMid, fontSize: "0.95rem", lineHeight: 1.7, margin: 0, maxWidth: "520px", fontFamily: theme.fontBody }}>{subtitle}</p>}
      </div>
    </div>
  );
}